"use client";

import clsx from "clsx";
import { useEffect, useState } from "react";

import { useInView } from "@client/functions/custom-hooks/useInView";

const AnimatedCounter = ({
  start = 0,
  end = 100,
  duration = 3000,
  className = "",
  style = {},
  intersectionOptions = {},
  boxStyle = {},
  showCurrency = false,
  currencySymbol = "$",
  showAbbreviation = false,
  abbreviationSymbol = "M",
}) => {
  const [counterRef, isVisible] = useInView(intersectionOptions);
  const [currentValue, setCurrentValue] = useState(start);

  const normalizedStartValue = start.toString().replace(".", "");
  const normalizedEndValue = end.toString().replace(".", "");
  const decimalIndex = end.toString().indexOf(".");
  const hasEndDecimal = decimalIndex > -1;

  useEffect(() => {
    if (!isVisible) return;

    const startTime = Date.now();
    const difference = +normalizedEndValue - +normalizedStartValue;

    const updateCounter = () => {
      const elapsed = Date.now() - startTime;
      const progress = Math.min(elapsed / duration, 1);

      // Easing function (exaggerated ease-out: quicker onset, slower exit)
      const easeOut = 1 - Math.pow(1 - progress, 5);
      const newValue = Math.round(start + difference * easeOut);

      setCurrentValue(newValue);

      if (progress < 1) {
        requestAnimationFrame(updateCounter);
      }
    };

    requestAnimationFrame(updateCounter);
  }, [isVisible, normalizedStartValue, normalizedEndValue, duration]);

  // Get the number of digits needed for the final number
  const maxDigits = Math.max(
    normalizedStartValue?.length,
    normalizedEndValue?.length,
  );

  // covert to string and identify the index of the decimal point

  // Pad the current value with leading zeros if needed
  const paddedValue = currentValue.toString().padStart(maxDigits, "0");
  const digits = paddedValue.split("");

  const defaultBoxStyle = clsx(
    "text-text-default text-6xl font-semibold bg-amber-400 rounded-lg shadow-sm min-w-[60px] inline-flex items-center justify-center p-2",
    boxStyle,
  );

  const decimalBoxStyle = clsx(
    "text-text-default text-6xl font-semibold rounded-lg inline-flex items-center justify-center mr-[-6px] opacity-0 duration-300 ease-in",
    boxStyle,
  );

  const containerStyle = clsx(
    "inline-flex items-center justify-center p-2 gap-2 w-full bg-amber-100 duration-300 ease-in",
    style,
  );

  return (
    <div ref={counterRef} className={clsx(containerStyle, className)}>
      {showCurrency && (
        <div className={clsx(defaultBoxStyle)}>{currencySymbol}</div>
      )}
      {digits.map((digit, index) => {
        if (hasEndDecimal && index === decimalIndex) {
          return (
            <div
              key={`${index}-decimal`}
              className={clsx(" flex ml-[-9px]", {
                "ml-[-6px]!": isVisible,
              })}
            >
              <div
                className={clsx(decimalBoxStyle, {
                  "animate-loaded": isVisible,
                  "mr-px!": isVisible,
                })}
              >
                .
              </div>
              <div className={clsx(defaultBoxStyle)}>{digit}</div>
            </div>
          );
        }
        return (
          <div key={`${index}-${maxDigits}`} className={clsx(defaultBoxStyle)}>
            {digit}
          </div>
        );
      })}
      {showAbbreviation && (
        <div key={"abbreviation-container"} className={clsx(defaultBoxStyle)}>
          {abbreviationSymbol}
        </div>
      )}
    </div>
  );
};

export default AnimatedCounter;
